import React, { createContext, useContext, ReactNode, useState, useMemo, useEffect, useCallback } from "react";
import { useSwivuChecklist, TUseSwivuChecklistReturn } from "@/react/hooks/entityHooks/useSwivuChecklist";
import { Checklist } from "@/bryntum-lib/models/Checklist";
import { useSwivuChecklistItems, TUseSwivuChecklistItemsReturn } from "@/react/hooks/entityHooks/useSwivuChecklistItems";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord, WidgetParamKeys } from "@/types/entity";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";

interface EntityChecklistContextValue extends TUseSwivuChecklistReturn, TUseSwivuChecklistItemsReturn {
  selectedChecklist: Checklist | null;
  newChecklist: Checklist | null;
  selectedChecklistItems: ChecklistItems[];
  isCreatingNewChecklist: boolean;
  handleShowChecklistDetails: (checklist: Checklist) => void;
  handleHideChecklistDetails: () => void;
  handleCloseEditor: () => void;
  persistNewChecklist: () => Promise<void>;
  hasChecklists: boolean;
  handleOpenEditor: (isCreatingNew: boolean, checklist?: Checklist) => void;
  canEditSelectedChecklist: boolean;
}

const EntityChecklistContext = createContext<EntityChecklistContextValue | undefined>(undefined);

interface EntityChecklistProviderProps {
  children: ReactNode;
  record: EntityRecord;
  initialChecklistId?: string | number;
  isCreatingNew?: boolean;
  onCloseEditor?: () => void;
}

export const EntityChecklistProvider: React.FC<EntityChecklistProviderProps> = ({
  children,
  record,
  initialChecklistId,
  isCreatingNew = false,
  onCloseEditor
}) => {
  const { project, editorHandler, user, entityManager } = useSwivuBryntumContext();
  const { callParamAction } = entityManager;
  const checklistOperations = useSwivuChecklist({ record });
  const { checklists, refreshChecklists, canViewChecklist } = checklistOperations;
  const checklistItemsOperations = useSwivuChecklistItems();
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  const [isCreatingNewChecklist, setIsCreatingNewChecklist] = useState(false);
  const [newChecklist, setNewChecklist] = useState<Checklist | null>(null);
  const { subscribe: subscribeChecklists, version: checklistVersion } = useSwivuStore<Checklist>({ storeId: STORE_KEYS.checklistStore });
  const { version: checklistItemsVersion, subscribe: subscribeChecklistItems } = useSwivuStore<ChecklistItems>({storeId: STORE_KEYS.checklistItemStore });

  useEffect(() => {
    if (initialChecklistId) {
      const checklistRecord = checklists.find((checklist) => checklist.id === initialChecklistId);
      if (checklistRecord) {
        setSelectedChecklist(checklistRecord);
      }
    }
  }, [initialChecklistId, checklists]);

  useEffect(() => {
    if (checklists.length === 1 && !isCreatingNew) {
      setSelectedChecklist(checklists[0]);
    }
  }, [checklists, isCreatingNew]);

  useEffect(() => {
    if (isCreatingNew) {
      const newChecklist = checklistOperations.createChecklist("", [], false);
      setNewChecklist(newChecklist);
    }
  }, [isCreatingNew]);

  const persistNewChecklist = async () => {
    if (!newChecklist || isCreatingNewChecklist) return;
    setIsCreatingNewChecklist(true);
    if (newChecklist.title.trim() === "") newChecklist.title = "New Checklist";
    project.checklistStore.add(newChecklist);
    await project.sync();
    setSelectedChecklist(newChecklist);
    setNewChecklist(null);
    setIsCreatingNewChecklist(false);
  };

  const handleShowChecklistDetails = useCallback((checklist: Checklist) => {
    setSelectedChecklist(checklist);
  }, []);

  const handleHideChecklistDetails = () => {
    setSelectedChecklist(null);
  };

  const handleCloseEditor = () => {
    onCloseEditor?.();
  };

  const handleOpenEditor = useCallback((isCreatingNew: boolean, checklist?: Checklist) => {
    editorHandler.openEditor(
      EDITOR_KEYS.checklistEditor,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      null,
      {
        entity: record,
        checklistId: checklist?.id || selectedChecklist?.id,
        isCreatingNew
      },
      { by: "recordType", useExisting: true }
    );
  }, [editorHandler, record, selectedChecklist?.id]);

  const selectedChecklistItems = useMemo(() => {
    if (!selectedChecklist) return [];
    return checklistItemsOperations.getChecklistItems(selectedChecklist.id);
  }, [selectedChecklist, checklistItemsOperations.getChecklistItems, checklistVersion, checklistItemsVersion]);

  useEffect(() => {
    return subscribeChecklistItems(({ record: changedRecord }) => {
      return changedRecord?.checklistRecord?.[record.entityType] === record?.id;
    });
  }, [subscribeChecklistItems, record])

  useEffect(() => {
    return subscribeChecklists(({ record: changedRecord, action }) => {
      if (changedRecord[record.entityType] === record.id) {
        refreshChecklists();

        const canView = canViewChecklist(changedRecord);
        if (selectedChecklist?.id === changedRecord.id) {
          if (action === "remove") {
            handleHideChecklistDetails();
            handleCloseEditor();
          }

          /* Close editor & checklist if user does not have view permission */
          if (!canView) {
            handleHideChecklistDetails();
            handleCloseEditor();
          }
        }
      }

      return true;
    });
  }, [subscribeChecklists, refreshChecklists, record, canViewChecklist]);

  useEffect(() => {
    return callParamAction(WidgetParamKeys.CHECKLIST.EDIT, (checklistId) => {
      const checklist = checklists.find((checklist) => checklist.id === checklistId);
      if (checklist) {
        handleOpenEditor(false, checklist);
        return true;
      }
      return false;
    });
  }, [callParamAction, checklists])

  const canEditSelectedChecklist = useMemo(() => {
    if (!selectedChecklist || !user) return false;
    return checklistOperations.canEditChecklist(selectedChecklist);
  }, [selectedChecklist, user, checklistVersion])

  const hasChecklists = checklists.length > 0;

  return (
    <EntityChecklistContext.Provider
      value={{
        ...checklistOperations,
        ...checklistItemsOperations,
        selectedChecklist,
        selectedChecklistItems,
        isCreatingNewChecklist,
        handleShowChecklistDetails,
        handleHideChecklistDetails,
        handleCloseEditor,
        persistNewChecklist,
        newChecklist,
        hasChecklists,
        handleOpenEditor,
        canEditSelectedChecklist
      }}
    >
      {children}
    </EntityChecklistContext.Provider>
  );
};

export function useEntityChecklistContext(): EntityChecklistContextValue {
  const context = useContext(EntityChecklistContext);
  if (!context) {
    throw new Error("useEntityChecklistContext must be used within EntityChecklistProvider");
  }
  return context;
}
